import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AggregateDataCheckbox as AggregateHiresCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

export function AggregateHiresControls() {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();
  const { setDisplayPrefs } = displayPrefs;
  const [open, setOpen] = useState(false);

  // Fetched after the funnel-details/channels-list data has settled — this
  // is a secondary "linked openings" checkbox, not needed until after
  // mount, so it doesn't need to race those fetches.
  const { data } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.hiringOpenings,
      openingSlug,
      hiringGoalId,
      displayPrefs.showAggregateData,
      startDate,
      endDate,
      filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug,
        hiringGoalId,
        startDate,
        endDate,
        filterRange,
      ),
    // `AggregateHiresControls` is rendered from both `DateRangeView` and
    // `HiringGoalView`; a Suspense retry elsewhere in the tree can remount
    // it shortly after a previous fetch resolved. A short staleTime avoids
    // treating that as "stale" and firing a redundant duplicate request.
    staleTime: 10 * 1000, // 10 seconds
  });

  const hiringOpenings = useMemo(
    () => data.hiring_openings?.filter(o => o.slug !== openingSlug) ?? [],
    [data, openingSlug],
  );

  if (hiringOpenings.length === 0) return null;

  const openingsLabel = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="link"
          className="text-fx-brand-600 h-auto p-0 font-normal underline hover:no-underline"
          onClick={e => e.stopPropagation()}
        >
          <FormattedMessage
            defaultMessage="{count, plural, one {# opening} other {# openings}}"
            description="Underlined link in aggregate hires label; {count} is linked openings"
            values={{ count: hiringOpenings.length }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-fit max-w-100 flex-col items-start justify-center"
      >
        {hiringOpenings.map(opening => (
          <div
            key={opening.external_id}
            className="flex w-full items-center justify-between py-2"
          >
            <Typography variant="subtitle1">{opening.title}</Typography>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );

  return (
    <AggregateHiresCheckbox
      checked={displayPrefs.showAggregateHires}
      onCheckedChange={value =>
        setDisplayPrefs({ showAggregateHires: !!value })
      }
      label={
        <FormattedMessage
          defaultMessage="Include aggregate hires ({openings})"
          description="Checkbox label with link to view linked openings; {openings} is the underlined count"
          values={{ openings: openingsLabel }}
        />
      }
      tooltipContent={intl.formatMessage({
        defaultMessage:
          'This is total number of hires across all linked openings.',
        description:
          'Explains the aggregate when multiple openings are linked in WX',
      })}
    />
  );
}

AggregateHiresControls.Skeleton = function AggregateHiresControlsSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="h-5 w-[178px]" />
    </div>
  );
};
